// ============================================================================
// OPENING STOCK (ADR-0058 §9, inventory slice 2) — quantities and costs of the
// stock held when the entity starts using inventory. One document per entry:
// header (date, reference, narration) + lines (item × location × pack × qty ×
// unit cost). Under perpetual / periodic inventory the server books the total
// Dr Inventory / Cr Opening Balance Difference on a Stock Valuation voucher.
// Posted on save; Edit re-posts; Cancel removes its stock and its valuation.
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn,
  SyutSelect, type SelectOption, ColumnSelector, type ColumnDefinition, useColumnVisibility,
} from 'components/ui';
import { EnterpriseScreenShell, actionButtonFactory, useCan } from 'components/enterprise-shell';
import { FyDateInput } from 'components/common/FyDateInput';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useNavTarget } from 'contexts/NavigationContext';
import { useAuthContext } from 'contexts/AuthContext';
import { useSelectedFinancialYear, defaultDateIn, isDateInYear } from 'hooks/useSelectedFinancialYear';
import { useOpeningStocks } from 'hooks/api';
import { openingStockService, itemService, stockLocationService } from 'api/services';
import type {
  OpeningStockDto, OpeningStockQueryParams, SaveOpeningStockRequest, ItemDto, ItemUomDto,
} from 'api/types/inventory.types';

type ViewMode = 'list' | 'grid';

const ICONS = IconSystem as unknown as Record<string, React.ComponentType<{ size?: number }>>;

interface ApiError { response?: { status?: number; data?: { message?: string } } }
const apiStatus = (err: unknown) => (err as ApiError | null)?.response?.status;
const apiMessage = (err: unknown, fallback: string) =>
  (err as ApiError | null)?.response?.data?.message || fallback;
const CONCURRENCY_MESSAGE =
  'This opening stock was changed by someone else since you opened it. Close the form and reopen it to get the latest version.';

const INFO_LINE =
  'Each line adds stock at the cost you enter. Under perpetual or periodic inventory the total is booked Dr Inventory / Cr Opening Balance Difference on a Stock Valuation voucher.';

const num = (s: string) => { const n = parseFloat(s); return isNaN(n) ? 0 : n; };
const money = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const qtyFmt = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 6 });
const fmtDate = (s?: string | null) => (s ? new Date(`${s.slice(0, 10)}T00:00:00`).toLocaleDateString() : '—');

const itemLabel = (code?: string | null, name?: string | null) =>
  code ? `${code} — ${name || ''}` : (name || '');
const locationLabel = (code?: string | null, name?: string | null) =>
  code ? `${code} — ${name || ''}` : (name || '');
const packLabel = (u: Pick<ItemUomDto, 'uomCode' | 'factorToBase'>) => `${u.uomCode || '?'} (×${qtyFmt(u.factorToBase)})`;

/** Default pack of a picked item: its purchase default, else the base unit. */
const defaultPack = (item: ItemDto): ItemUomDto | undefined =>
  item.uoms.find(u => u.isPurchaseDefault)
  ?? item.uoms.find(u => u.isBase)
  ?? item.uoms.find(u => u.uomId === item.baseUomId)
  ?? item.uoms[0];

const statusBadge = (s: OpeningStockDto['docStatus']) => (
  <span className="badge" style={{ background: s === 'CANCELLED' ? '#6b7280' : '#16a34a', color: '#fff' }}>
    {s === 'CANCELLED' ? 'Cancelled' : 'Posted'}
  </span>
);

// ---------------------------------------------------------------------------
// FORM STATE
// ---------------------------------------------------------------------------

interface HeaderForm {
  documentDate: string;
  reference: string;
  narration: string;
}

interface LineRow {
  key: number;
  itemId: string;
  stockLocationId: string;
  uomId: string;
  quantity: string;
  unitCost: string;
  narration: string;
  // Display caches (not part of the dirty snapshot).
  itemText: string;
  locationText: string;
  uomCode: string;
  factorToBase: number;
  baseUomCode: string;
}

/** Only user-editable fields — label caches fill in asynchronously and must not mark the form dirty. */
const snapshotOf = (f: HeaderForm, l: LineRow[]) => JSON.stringify({
  f,
  l: l.map(x => ({
    itemId: x.itemId, stockLocationId: x.stockLocationId, uomId: x.uomId,
    quantity: x.quantity, unitCost: x.unitCost, narration: x.narration,
  })),
});

const isScratch = (l: LineRow) => !l.itemId && !l.stockLocationId && !l.quantity && !l.unitCost && !l.narration.trim();

// ---------------------------------------------------------------------------
// ENTRY MODAL
// ---------------------------------------------------------------------------

interface OpeningStockFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  record: OpeningStockDto | null;
  onSaved: () => void;
  onClose: () => void;
  tabId?: string;
  moduleName: string;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
  onRestoreModal?: (mode: 'create' | 'edit', instanceId?: string, documentNumber?: string) => void;
}

const OpeningStockFormModal: React.FC<OpeningStockFormModalProps> = ({
  isOpen, mode, record, onSaved, onClose,
  tabId, moduleName, createInstanceId, minimizeRef, onRestoreModal,
}) => {
  const isEdit = mode === 'edit';
  const { minimize } = useModalMinimize();
  const { selectedEntity, selectedBusinessUnit, selectedFinancialYear } = useAuthContext();
  const fy = useSelectedFinancialYear();
  const fyRef = useRef(fy); fyRef.current = fy;

  const lineKey = useRef(0);
  const blankLine = useCallback((): LineRow => ({
    key: ++lineKey.current, itemId: '', stockLocationId: '', uomId: '', quantity: '', unitCost: '', narration: '',
    itemText: '', locationText: '', uomCode: '', factorToBase: 1, baseUomCode: '',
  }), []);
  const initialHeader = useCallback((): HeaderForm => ({
    documentDate: defaultDateIn(fyRef.current), reference: '', narration: '',
  }), []);

  const [form, setForm] = useState<HeaderForm>(initialHeader);
  const [lines, setLines] = useState<LineRow[]>(() => [blankLine()]);
  /** Picked items' detail reads (packs + entity setting), by item id. */
  const [itemDetails, setItemDetails] = useState<Record<string, ItemDto>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loadingRecord, setLoadingRecord] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [focusKey, setFocusKey] = useState<number | null>(null);

  const formRef = useRef(form); formRef.current = form;
  const linesRef = useRef(lines); linesRef.current = lines;
  const itemDetailsRef = useRef(itemDetails); itemDetailsRef.current = itemDetails;
  const modeRef = useRef(mode); modeRef.current = mode;
  const recordRef = useRef(record); recordRef.current = record;
  const createInstanceIdRef = useRef(createInstanceId); createInstanceIdRef.current = createInstanceId;
  const editIdRef = useRef<string | null>(null);
  const rowVersionRef = useRef(0);
  const documentNumberRef = useRef('');
  const initialSnapshotRef = useRef('');
  const isRestoreRef = useRef(false);
  const closeAfterRef = useRef(true);

  const itemLabelCache = useRef<Map<string, string>>(new Map());
  const locationLabelCache = useRef<Map<string, string>>(new Map());

  /** Best-effort detail reads for items already on the form (edit) — packs for the Unit picker. */
  const loadItemDetails = useCallback(async (ids: string[]) => {
    const missing = Array.from(new Set(ids.filter(id => id && !itemDetailsRef.current[id])));
    if (!missing.length) return;
    const results = await Promise.all(missing.map(id => itemService.getById(id).catch(() => null)));
    setItemDetails(prev => {
      const next = { ...prev };
      results.forEach(it => { if (it) next[it.id] = it; });
      return next;
    });
  }, []);

  // Populate on open (restore keeps the parked session as is).
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) { isRestoreRef.current = false; setFieldErrors({}); return; }
    const cancelToken = { cancelled: false };
    setFieldErrors({});
    setFocusKey(null);
    const r = recordRef.current;
    if (modeRef.current === 'edit' && r) {
      editIdRef.current = r.id;
      rowVersionRef.current = r.rowVersion;
      documentNumberRef.current = r.documentNumber;
      setLoadingRecord(true);
      openingStockService.getById(r.id).then(doc => {
        if (cancelToken.cancelled) return;
        const f: HeaderForm = {
          documentDate: (doc.documentDate || '').slice(0, 10),
          reference: doc.reference || '',
          narration: doc.narration || '',
        };
        const l: LineRow[] = (doc.lines || []).slice().sort((a, b) => a.lineNo - b.lineNo).map(ln => {
          const iText = itemLabel(ln.itemCode, ln.itemName);
          const lText = locationLabel(ln.locationCode, ln.locationName);
          itemLabelCache.current.set(ln.itemId, iText);
          locationLabelCache.current.set(ln.stockLocationId, lText);
          return {
            key: ++lineKey.current,
            itemId: ln.itemId, stockLocationId: ln.stockLocationId, uomId: ln.uomId,
            quantity: String(ln.quantity), unitCost: String(ln.unitCost), narration: ln.narration || '',
            itemText: iText, locationText: lText, uomCode: ln.uomCode || '',
            factorToBase: ln.factorToBase || 1, baseUomCode: ln.baseUomCode || '',
          };
        });
        const rows = l.length ? l : [blankLine()];
        rowVersionRef.current = doc.rowVersion;
        documentNumberRef.current = doc.documentNumber;
        setForm(f);
        setLines(rows);
        initialSnapshotRef.current = snapshotOf(f, rows);
        void loadItemDetails(rows.map(x => x.itemId));
      }).catch((err: unknown) => {
        if (cancelToken.cancelled) return;
        notificationService.toast.error(apiMessage(err, 'Could not load the opening stock.'));
      }).finally(() => { if (!cancelToken.cancelled) setLoadingRecord(false); });
    } else {
      editIdRef.current = null;
      rowVersionRef.current = 0;
      documentNumberRef.current = '';
      const f = initialHeader();
      const rows = [blankLine()];
      setForm(f);
      setLines(rows);
      initialSnapshotRef.current = snapshotOf(f, rows);
    }
    return () => { cancelToken.cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return snapshotOf(form, lines) !== initialSnapshotRef.current;
  }, [form, lines]);

  const totalValue = useMemo(() => lines.reduce((s, l) => s + num(l.quantity) * num(l.unitCost), 0), [lines]);

  const clearFieldError = useCallback((f: string) => {
    setFieldErrors(prev => {
      if (!prev[f]) return prev;
      const next = { ...prev }; delete next[f]; return next;
    });
  }, []);

  const updateHeader = useCallback((patch: Partial<HeaderForm>) => setForm(p => ({ ...p, ...patch })), []);
  const updateLine = useCallback((key: number, patch: Partial<LineRow>) =>
    setLines(prev => prev.map(l => (l.key === key ? { ...l, ...patch } : l))), []);
  const addLine = useCallback((focus = false) => {
    const row = blankLine();
    setLines(prev => [...prev, row]);
    clearFieldError('lines');
    if (focus) setFocusKey(row.key);
  }, [blankLine, clearFieldError]);
  const removeLine = useCallback((key: number) =>
    setLines(prev => (prev.length > 1 ? prev.filter(l => l.key !== key) : [blankLine()])), [blankLine]);

  // --- Pickers (try/catch → [] so a refused lookup leaves an empty list, never a crash) ---
  const loadItems = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await itemService.getAll({ searchTerm: input || undefined, pageSize: 30, itemType: 'STOCK' });
      return res.data.map(it => {
        const label = itemLabel(it.itemCode, it.itemName);
        itemLabelCache.current.set(it.id, label);
        return { value: it.id, label };
      });
    } catch {
      return [];
    }
  }, []);

  const loadLocations = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await stockLocationService.lookup({ searchTerm: input || undefined, pageSize: 50 });
      return res.data.map(loc => {
        const label = locationLabel(loc.locationCode, loc.locationName);
        locationLabelCache.current.set(loc.id, label);
        return { value: loc.id, label };
      });
    } catch {
      return [];
    }
  }, []);

  /** Item picked: reset its unit, then read the item for its packs, default pack and default location. */
  const onPickItem = useCallback(async (key: number, itemId: string) => {
    clearFieldError(`line-${key}-item`);
    updateLine(key, {
      itemId, itemText: (itemId && itemLabelCache.current.get(itemId)) || '',
      uomId: '', uomCode: '', factorToBase: 1, baseUomCode: '',
    });
    if (!itemId) return;
    let item = itemDetailsRef.current[itemId];
    if (!item) {
      try {
        item = await itemService.getById(itemId);
        setItemDetails(prev => ({ ...prev, [itemId]: item }));
      } catch (err: unknown) {
        notificationService.toast.error(apiMessage(err, 'Could not load the item\'s units.'));
        return;
      }
    }
    const pack = defaultPack(item);
    const defLocId = item.entitySetting?.defaultStockLocationId || '';
    const defLocText = locationLabel(item.entitySetting?.defaultStockLocationCode, item.entitySetting?.defaultStockLocationName);
    if (defLocId && defLocText) locationLabelCache.current.set(defLocId, defLocText);
    setLines(prev => prev.map(l => {
      if (l.key !== key || l.itemId !== itemId) return l; // the row moved on meanwhile
      return {
        ...l,
        uomId: pack?.uomId || '',
        uomCode: pack?.uomCode || '',
        factorToBase: pack?.factorToBase || 1,
        baseUomCode: item.baseUomCode || '',
        ...(!l.stockLocationId && defLocId ? { stockLocationId: defLocId, locationText: defLocText } : {}),
      };
    }));
    if (pack) clearFieldError(`line-${key}-uom`);
    if (defLocId) clearFieldError(`line-${key}-location`);
  }, [updateLine, clearFieldError]);

  const onPickUom = useCallback((key: number, itemId: string, uomId: string) => {
    clearFieldError(`line-${key}-uom`);
    const pack = itemDetailsRef.current[itemId]?.uoms.find(u => u.uomId === uomId);
    updateLine(key, { uomId, uomCode: pack?.uomCode || '', factorToBase: pack?.factorToBase || 1 });
  }, [updateLine, clearFieldError]);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardOpen(true); else onClose();
  }, [isDirty, onClose]);

  const handleMinimize = useCallback(() => {
    if (!tabId) return;
    const currentMode = modeRef.current;
    const editing = currentMode === 'edit';
    const cId = createInstanceIdRef.current || '1';
    const docNo = documentNumberRef.current || recordRef.current?.documentNumber || '';
    const f = { ...formRef.current };
    const l = linesRef.current.map(x => ({ ...x }));
    const savedSnapshot = initialSnapshotRef.current;
    const savedEditId = editIdRef.current;
    const savedRowVersion = rowVersionRef.current;
    const currentIsDirty = savedSnapshot ? snapshotOf(f, l) !== savedSnapshot : false;
    const modalId = editing ? `${tabId}-form-modal-edit-${docNo}` : `${tabId}-form-modal-create-${cId}`;
    const title = editing ? `Edit Opening Stock - ${docNo}` : `New Opening Stock (${cId})`;
    minimize({
      id: modalId, title, moduleName, tabId, isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setForm(f);
        setLines(l);
        initialSnapshotRef.current = savedSnapshot;
        editIdRef.current = savedEditId;
        rowVersionRef.current = savedRowVersion;
        documentNumberRef.current = docNo;
        onRestoreModal?.(currentMode, editing ? undefined : cId, editing ? docNo : undefined);
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const validate = useCallback((): Record<string, string> => {
    const e: Record<string, string> = {};
    if (!form.documentDate) e.documentDate = 'Document date is required';
    else if (fy?.isClosed) e.documentDate = `Financial year ${fy.name} is closed.`;
    else if (!isDateInYear(form.documentDate, fy)) e.documentDate = 'The date is outside the selected financial year.';
    if (form.reference.length > 60) e.reference = 'Reference can be at most 60 characters';
    const real = lines.filter(l => !isScratch(l));
    if (real.length === 0) e.lines = 'Add at least one line.';
    real.forEach(l => {
      if (!l.itemId) e[`line-${l.key}-item`] = 'Required';
      if (!l.stockLocationId) e[`line-${l.key}-location`] = 'Required';
      if (!l.uomId) e[`line-${l.key}-uom`] = 'Required';
      if (!(num(l.quantity) > 0)) e[`line-${l.key}-quantity`] = 'Must be more than 0';
      if (l.unitCost === '' || isNaN(parseFloat(l.unitCost)) || num(l.unitCost) < 0) e[`line-${l.key}-unitCost`] = '0 or more';
    });
    return e;
  }, [form, lines, fy]);

  const handleSave = useCallback((closeAfter: boolean) => {
    if (!selectedEntity || !selectedBusinessUnit || !selectedFinancialYear) {
      notificationService.toast.error('Select an entity, business unit, and financial year first.');
      return;
    }
    const e = validate();
    if (Object.keys(e).length) {
      setFieldErrors(e);
      const first = e.documentDate || e.reference || e.lines
        || 'Complete every line: item, location and unit; a quantity above 0 and a unit cost of 0 or more.';
      notificationService.toast.warning(first);
      return;
    }
    setFieldErrors({});
    closeAfterRef.current = closeAfter;
    setSaveConfirmOpen(true);
  }, [validate, selectedEntity, selectedBusinessUnit, selectedFinancialYear]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setSaving(true);
    const editing = modeRef.current === 'edit' && !!editIdRef.current;
    try {
      const payload: SaveOpeningStockRequest = {
        documentDate: form.documentDate,
        reference: form.reference.trim() || null,
        narration: form.narration.trim() || null,
        lines: lines.filter(l => !isScratch(l)).map(l => ({
          itemId: l.itemId,
          stockLocationId: l.stockLocationId,
          uomId: l.uomId,
          quantity: num(l.quantity),
          unitCost: num(l.unitCost),
          narration: l.narration.trim() || null,
        })),
      };
      const saved = editing && editIdRef.current
        ? await openingStockService.update(editIdRef.current, { ...payload, rowVersion: rowVersionRef.current })
        : await openingStockService.create(payload);
      setSaving(false);
      const done = `Opening stock ${saved.documentNumber} ${editing ? 'updated' : 'posted'}.`;
      const warnings = saved.warnings || [];
      if (warnings.length) notificationService.toast.warning(`${done} ${warnings.join(' ')}`, 10000);
      else notificationService.toast.success(done);
      onSaved();
      if (closeAfterRef.current || editing) {
        initialSnapshotRef.current = snapshotOf(form, lines);
        onClose();
      } else {
        // Save & New: keep the date, start a fresh set of lines.
        const f: HeaderForm = { documentDate: form.documentDate, reference: '', narration: '' };
        const rows = [blankLine()];
        setForm(f);
        setLines(rows);
        initialSnapshotRef.current = snapshotOf(f, rows);
      }
    } catch (err: unknown) {
      setSaving(false);
      if (apiStatus(err) === 409) {
        notificationService.toast.error(apiMessage(err, CONCURRENCY_MESSAGE));
        onSaved();
      } else {
        notificationService.toast.error(apiMessage(err, `Could not ${editing ? 'update' : 'post'} the opening stock.`));
      }
    }
  }, [form, lines, onSaved, onClose, blankLine]);

  if (!isOpen) return null;

  const err = (f: string) => fieldErrors[f];
  const cellInput: React.CSSProperties = { width: '100%', fontSize: '0.8125rem', padding: '0.375rem 0.5rem' };
  const th: React.CSSProperties = { padding: '0.5rem', textAlign: 'left', whiteSpace: 'nowrap' };
  const td: React.CSSProperties = { padding: '0.25rem 0.375rem', verticalAlign: 'top' };
  const readCell: React.CSSProperties = { ...td, paddingTop: '0.55rem', textAlign: 'right', whiteSpace: 'nowrap' };
  const scopeBadge: React.CSSProperties = {
    background: 'var(--bg-secondary)', color: 'var(--text-secondary)', border: '1px solid var(--border-color)', fontWeight: 500,
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl">
        {(saving || loadingRecord) && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              {saving ? 'Posting...' : 'Loading...'}
            </span>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {isEdit ? `Edit Opening Stock ${documentNumberRef.current || record?.documentNumber || ''}` : 'New Opening Stock'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={saving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={saving}><IconSystem.x size={20} /></button>
          </div>
        </div>

        <div className="modal-content">
          {/* Scope the document posts to (taken from the header selection, sent as request headers). */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', marginBottom: '0.75rem' }}>
            <span className="badge" style={scopeBadge} title="Entity">Entity: {selectedEntity?.name || '—'}</span>
            <span className="badge" style={scopeBadge} title="Business unit">BU: {selectedBusinessUnit?.name || '—'}</span>
            <span className="badge" style={scopeBadge} title="Financial year">FY: {fy?.name || selectedFinancialYear?.name || '—'}</span>
          </div>

          <div className="form-grid-3">
            <div className="form-group" data-field="documentDate">
              <label className="form-label">Document Date <span className="text-danger">*</span></label>
              <FyDateInput
                className={`form-input${err('documentDate') ? ' form-field-error' : ''}`}
                value={form.documentDate}
                onChange={v => { clearFieldError('documentDate'); updateHeader({ documentDate: v }); }}
              />
              {err('documentDate') && <div className="form-error-message">{err('documentDate')}</div>}
            </div>
            <div className="form-group" data-field="reference">
              <label className="form-label">Reference</label>
              <input
                type="text"
                className={`form-input${err('reference') ? ' form-field-error' : ''}`}
                value={form.reference}
                maxLength={60}
                placeholder="e.g. stock count sheet no."
                onChange={e => { clearFieldError('reference'); updateHeader({ reference: e.target.value }); }}
              />
              {err('reference') && <div className="form-error-message">{err('reference')}</div>}
            </div>
            <div className="form-group" data-field="narration">
              <label className="form-label">Narration</label>
              <input
                type="text"
                className="form-input"
                value={form.narration}
                placeholder="e.g. Stock on hand at go-live"
                onChange={e => updateHeader({ narration: e.target.value })}
              />
            </div>
          </div>

          <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem', marginTop: '0.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', gap: '0.75rem' }}>
              <span className="form-label" style={{ margin: 0 }}>Lines</span>
              <button className="btn btn-secondary" onClick={() => addLine(true)} style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem' }}>
                <IconSystem.add size={12} /> Add line
              </button>
            </div>
            <div style={{
              display: 'flex', gap: '0.5rem', alignItems: 'flex-start', padding: '0.5rem 0.75rem',
              marginBottom: '0.5rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)',
              background: 'var(--bg-secondary)', color: 'var(--text-secondary)', fontSize: '0.78rem',
            }}>
              <IconSystem.info size={15} />
              <span>{INFO_LINE}</span>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table className="syut-grid" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem', minWidth: 1100 }}>
                <thead>
                  <tr style={{ background: 'var(--grid-header-bg, #dbeafe)' }}>
                    <th style={{ ...th, width: 32, textAlign: 'center' }}>#</th>
                    <th style={{ ...th, minWidth: 230 }}>Item <span className="text-danger">*</span></th>
                    <th style={{ ...th, minWidth: 190 }}>Location <span className="text-danger">*</span></th>
                    <th style={{ ...th, width: 140 }}>Unit <span className="text-danger">*</span></th>
                    <th style={{ ...th, width: 100, textAlign: 'right' }}>Quantity <span className="text-danger">*</span></th>
                    <th style={{ ...th, width: 110, textAlign: 'right' }}>Unit Cost <span className="text-danger">*</span></th>
                    <th style={{ ...th, width: 120, textAlign: 'right' }}>Value</th>
                    <th style={{ ...th, width: 120, textAlign: 'right' }}>Base Qty</th>
                    <th style={{ ...th, minWidth: 150 }}>Narration</th>
                    <th style={{ ...th, width: 36 }} />
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, idx) => {
                    const item = l.itemId ? itemDetails[l.itemId] : undefined;
                    const packOptions: SelectOption[] = item
                      ? item.uoms.slice().sort((a, b) => a.displayOrder - b.displayOrder).map(u => ({ value: u.uomId, label: packLabel(u) }))
                      : (l.uomId ? [{ value: l.uomId, label: packLabel({ uomCode: l.uomCode, factorToBase: l.factorToBase }) }] : []);
                    const lineValue = num(l.quantity) * num(l.unitCost);
                    const baseQty = num(l.quantity) * (l.factorToBase || 1);
                    const baseCode = l.baseUomCode || item?.baseUomCode || '';
                    const isLast = idx === lines.length - 1;
                    return (
                      <tr key={l.key} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={{ ...td, textAlign: 'center', color: 'var(--text-muted)', paddingTop: '0.55rem' }}>{idx + 1}</td>
                        <td style={td}>
                          <SyutSelect isAsync loadOptions={loadItems} defaultOptions cacheOptions={false}
                            seedOptions={l.itemId ? [{ value: l.itemId, label: l.itemText || itemLabelCache.current.get(l.itemId) || '' }] : undefined}
                            value={l.itemId} onChange={v => { void onPickItem(l.key, (v as string) || ''); }}
                            placeholder="Pick a stock item" isClearable size="sm" autoFocus={focusKey === l.key}
                            menuPortalTarget={document.body} error={err(`line-${l.key}-item`)} />
                        </td>
                        <td style={td}>
                          <SyutSelect isAsync loadOptions={loadLocations} defaultOptions cacheOptions={false}
                            seedOptions={l.stockLocationId ? [{ value: l.stockLocationId, label: l.locationText || locationLabelCache.current.get(l.stockLocationId) || '' }] : undefined}
                            value={l.stockLocationId}
                            onChange={v => {
                              const id = (v as string) || '';
                              clearFieldError(`line-${l.key}-location`);
                              updateLine(l.key, { stockLocationId: id, locationText: (id && locationLabelCache.current.get(id)) || '' });
                            }}
                            placeholder="Location" isClearable size="sm"
                            menuPortalTarget={document.body} error={err(`line-${l.key}-location`)} />
                        </td>
                        <td style={td}>
                          <SyutSelect options={packOptions} value={l.uomId}
                            onChange={v => onPickUom(l.key, l.itemId, (v as string) || '')}
                            placeholder={l.itemId ? 'Unit' : 'Pick item first'} isDisabled={!l.itemId}
                            isClearable={false} size="sm" menuPortalTarget={document.body} error={err(`line-${l.key}-uom`)} />
                        </td>
                        <td style={td}>
                          <input type="number" inputMode="decimal" min="0"
                            className={`form-input${err(`line-${l.key}-quantity`) ? ' form-field-error' : ''}`}
                            style={{ ...cellInput, textAlign: 'right' }} value={l.quantity} placeholder="0"
                            title={err(`line-${l.key}-quantity`)}
                            onChange={e => { clearFieldError(`line-${l.key}-quantity`); updateLine(l.key, { quantity: e.target.value }); }} />
                        </td>
                        <td style={td}>
                          <input type="number" inputMode="decimal" min="0"
                            className={`form-input${err(`line-${l.key}-unitCost`) ? ' form-field-error' : ''}`}
                            style={{ ...cellInput, textAlign: 'right' }} value={l.unitCost} placeholder="0.00"
                            title={err(`line-${l.key}-unitCost`) || (l.uomCode ? `Cost of one ${l.uomCode}` : undefined)}
                            onChange={e => { clearFieldError(`line-${l.key}-unitCost`); updateLine(l.key, { unitCost: e.target.value }); }} />
                        </td>
                        <td style={readCell}>{money(lineValue)}</td>
                        <td style={{ ...readCell, color: 'var(--text-secondary)' }}>
                          {l.quantity ? `${qtyFmt(baseQty)}${baseCode ? ` ${baseCode}` : ''}` : '—'}
                        </td>
                        <td style={td}>
                          <input type="text" className="form-input" style={cellInput} value={l.narration}
                            placeholder={isLast ? 'Enter adds a line' : ''}
                            onChange={e => updateLine(l.key, { narration: e.target.value })}
                            onKeyDown={e => {
                              if (!isLast) return;
                              if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey)) { e.preventDefault(); addLine(true); }
                            }} />
                        </td>
                        <td style={{ ...td, textAlign: 'center' }}>
                          <button title="Remove line" onClick={() => removeLine(l.key)}
                            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0.3rem' }}>
                            <IconSystem.trash size={15} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ fontWeight: 700, background: 'var(--bg-secondary)', position: 'sticky', bottom: 0 }}>
                    <td colSpan={6} style={{ textAlign: 'right', padding: '0.5rem' }}>Total value</td>
                    <td style={{ textAlign: 'right', padding: '0.5rem', whiteSpace: 'nowrap' }}>{money(totalValue)}</td>
                    <td colSpan={3} />
                  </tr>
                </tfoot>
              </table>
            </div>
            {err('lines') && <div className="form-error-message">{err('lines')}</div>}
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Cancel</button>
          {!isEdit && (
            <button className="btn btn-outline-primary" onClick={() => handleSave(false)} disabled={saving || loadingRecord}>
              Save &amp; New
            </button>
          )}
          <button className="btn btn-primary" onClick={() => handleSave(true)} disabled={saving || loadingRecord}>
            <IconSystem.check size={16} /> {isEdit ? 'Save Changes' : 'Save & Close'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={isEdit ? 'Save Opening Stock' : 'Post Opening Stock'}
        message={isEdit
          ? `Save changes to ${documentNumberRef.current} (total ${money(totalValue)})? Later documents are re-costed.`
          : `Post this opening stock of ${lines.filter(l => !isScratch(l)).length} line(s), total ${money(totalValue)}?`}
        confirmLabel={isEdit ? 'Save' : 'Post'}
        cancelLabel="Cancel"
        variant="default"
        onConfirm={() => { void handleConfirmSave(); }}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardOpen(false); onClose(); }}
        onCancel={() => setDiscardOpen(false)}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// VIEW MODAL
// ---------------------------------------------------------------------------

const ViewField: React.FC<{ label: string; value?: React.ReactNode; fullWidth?: boolean }> = ({ label, value, fullWidth }) => (
  <div style={fullWidth ? { gridColumn: '1 / -1' } : {}}>
    <label className="form-label">{label}</label>
    <div style={{
      padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem',
      border: '1px solid var(--form-border-color)', fontSize: '0.875rem', minHeight: '36px', whiteSpace: 'pre-wrap',
    }}>
      {value !== null && value !== undefined && value !== '' ? value : <span style={{ color: 'var(--text-muted)' }}>—</span>}
    </div>
  </div>
);

interface OpeningStockViewModalProps {
  isOpen: boolean;
  record: OpeningStockDto | null;
  onClose: () => void;
  onEdit?: (r: OpeningStockDto) => void;
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (documentNumber: string) => void;
}

const OpeningStockViewModal: React.FC<OpeningStockViewModalProps> = ({
  isOpen, record, onClose, onEdit, tabId, moduleName, onRestoreView,
}) => {
  const { minimize } = useModalMinimize();
  const [detail, setDetail] = useState<OpeningStockDto | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !record?.id) { setDetail(null); return; }
    let cancelled = false;
    setLoading(true);
    openingStockService.getById(record.id)
      .then(d => { if (!cancelled) setDetail(d); })
      .catch((err: unknown) => {
        if (cancelled) return;
        setDetail(null);
        notificationService.toast.error(apiMessage(err, 'Could not load the opening stock lines.'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, record]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !record) return;
    minimize({
      id: `${tabId}-view-modal-${record.documentNumber}`,
      title: `View - ${record.documentNumber}`,
      moduleName, tabId,
      onRestore: () => onRestoreView?.(record.documentNumber),
    });
    onClose();
  }, [tabId, moduleName, record, minimize, onClose, onRestoreView]);

  if (!isOpen || !record) return null;
  const d = detail ?? record;
  const th: React.CSSProperties = { padding: '0.5rem', textAlign: 'left', whiteSpace: 'nowrap' };
  const tdR: React.CSSProperties = { padding: '0.375rem 0.5rem', textAlign: 'right', whiteSpace: 'nowrap' };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Opening Stock Details</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
          </div>
        </div>

        <div className="modal-content">
          <div className="form-grid-4" style={{ marginBottom: '1rem' }}>
            <ViewField label="Number" value={d.documentNumber} />
            <ViewField label="Date" value={fmtDate(d.documentDate)} />
            <ViewField label="Business Unit" value={d.businessUnitName} />
            <div>
              <label className="form-label">Status</label>
              <div style={{ paddingTop: '4px' }}>{statusBadge(d.docStatus)}</div>
            </div>
            <ViewField label="Reference" value={d.reference} />
            <ViewField label="Valuation Voucher" value={d.valuationTransactionNo} />
            <ViewField label="Total Value" value={money(d.totalValue)} />
            <ViewField label="Lines" value={d.lineCount} />
            <ViewField label="Narration" value={d.narration} fullWidth />
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table className="syut-grid" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead>
                <tr style={{ background: 'var(--grid-header-bg, #dbeafe)' }}>
                  <th style={{ ...th, width: 32, textAlign: 'center' }}>#</th>
                  <th style={th}>Item</th>
                  <th style={th}>Location</th>
                  <th style={th}>Unit</th>
                  <th style={{ ...th, textAlign: 'right' }}>Quantity</th>
                  <th style={{ ...th, textAlign: 'right' }}>Base Qty</th>
                  <th style={{ ...th, textAlign: 'right' }}>Unit Cost</th>
                  <th style={{ ...th, textAlign: 'right' }}>Value</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)' }}>Loading…</td></tr>
                )}
                {!loading && (d.lines || []).slice().sort((a, b) => a.lineNo - b.lineNo).map((l, idx) => (
                  <tr key={l.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{idx + 1}</td>
                    <td style={{ padding: '0.375rem 0.5rem' }}>
                      {itemLabel(l.itemCode, l.itemName)}
                      {l.narration && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{l.narration}</div>}
                    </td>
                    <td style={{ padding: '0.375rem 0.5rem' }}>{locationLabel(l.locationCode, l.locationName) || '—'}</td>
                    <td style={{ padding: '0.375rem 0.5rem' }}>{packLabel({ uomCode: l.uomCode, factorToBase: l.factorToBase })}</td>
                    <td style={tdR}>{qtyFmt(l.quantity)}</td>
                    <td style={tdR}>{qtyFmt(l.baseQuantity)}{l.baseUomCode ? ` ${l.baseUomCode}` : ''}</td>
                    <td style={tdR}>{money(l.unitCost)}</td>
                    <td style={tdR}>{money(l.value)}</td>
                  </tr>
                ))}
                {!loading && !detail && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)' }}>Lines could not be loaded.</td></tr>
                )}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 700, background: 'var(--bg-secondary)' }}>
                  <td colSpan={7} style={{ textAlign: 'right', padding: '0.5rem' }}>Total value</td>
                  <td style={{ textAlign: 'right', padding: '0.5rem', whiteSpace: 'nowrap' }}>{money(d.totalValue)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="form-grid-4" style={{ marginTop: '1rem' }}>
            <ViewField label="Created By" value={d.createdByName} />
            <ViewField label="Created At" value={d.createdAt ? new Date(d.createdAt).toLocaleString() : null} />
            <ViewField label="Updated By" value={d.updatedAt ? d.updatedByName : null} />
            <ViewField label="Updated At" value={d.updatedAt ? new Date(d.updatedAt).toLocaleString() : null} />
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          {onEdit && d.docStatus === 'POSTED' && (
            <button className="btn btn-primary" onClick={() => { onClose(); onEdit(record); }}>
              <IconSystem.edit size={16} /> Edit
            </button>
          )}
        </div>
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// SCREEN
// ---------------------------------------------------------------------------

const STRUCTURAL_COLUMN_IDS = ['select', 'actions'];

const COLUMN_DEFS: ColumnDefinition[] = [
  { id: 'select', label: '', required: true },
  { id: 'documentNumber', label: 'Number' },
  { id: 'documentDate', label: 'Date' },
  { id: 'businessUnit', label: 'Business unit' },
  { id: 'reference', label: 'Reference' },
  { id: 'lineCount', label: 'Lines' },
  { id: 'totalValue', label: 'Value' },
  { id: 'valuationTransactionNo', label: 'Valuation voucher' },
  { id: 'docStatus', label: 'Status' },
  { id: 'actions', label: 'Actions', required: true },
];

interface OpeningStockScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const OpeningStockScreen: React.FC<OpeningStockScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const can = useCan('opening-stock');

  const [queryParams, setQueryParams] = useState<OpeningStockQueryParams>({ page: 1, pageSize: 25 });
  const { data, loading, pagination, refetch } = useOpeningStocks(queryParams);

  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(
      () => setQueryParams(p => ({ ...p, searchTerm: searchQuery || undefined, page: 1 })),
      400,
    );
    return () => clearTimeout(t);
  }, [searchQuery]);

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const { visibleColumns, onApply: applyColumns, onReset: resetColumns } =
    useColumnVisibility('opening-stock-columns', COLUMN_DEFS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [includeCancelled, setIncludeCancelled] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selected, setSelected] = useState<OpeningStockDto | null>(null);
  const [createInstanceId, setCreateInstanceId] = useState<string>('1');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewRecord, setViewRecord] = useState<OpeningStockDto | null>(null);

  const [cancelTarget, setCancelTarget] = useState<OpeningStockDto | null>(null);
  const [cancelling, setCancelling] = useState(false);

  const handleAdd = useCallback(() => {
    if (formOpen) formModalMinimizeRef.current?.();
    let next = 1;
    if (tabId) { while (isMinimized(`${tabId}-form-modal-create-${next}`)) next++; }
    setCreateInstanceId(String(next));
    setFormMode('create'); setSelected(null); setFormOpen(true);
  }, [formOpen, tabId, isMinimized]);

  const handleEdit = useCallback((r: OpeningStockDto) => {
    if (r.docStatus !== 'POSTED') return;
    const modalId = tabId ? `${tabId}-form-modal-edit-${r.documentNumber}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId); modal?.onRestore?.();
      return;
    }
    if (formOpen) formModalMinimizeRef.current?.();
    setFormMode('edit'); setSelected(r); setFormOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal, formOpen]);

  const handleView = useCallback((r: OpeningStockDto) => {
    const viewId = tabId ? `${tabId}-view-modal-${r.documentNumber}` : null;
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismissMinimized(viewId); modal?.onRestore?.();
      return;
    }
    setViewRecord(r); setViewOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  // Drill from another screen (the Stock Card opens an opening stock by id): load it and show it read-only.
  useNavTarget('opening-stock', (t) => {
    openingStockService.getById(t.recordId)
      .then((full) => handleView(full))
      .catch(() => notificationService.toast.error('Could not open the opening stock.'));
  });

  const handleRestoreView = useCallback((docNo: string) => {
    const rec = data.find(r => r.documentNumber === docNo);
    if (rec) { setViewRecord(rec); setViewOpen(true); }
  }, [data]);

  const handleRestoreModal = useCallback(
    (m: 'create' | 'edit', instanceId?: string, docNo?: string) => {
      if (m === 'create' && instanceId) setCreateInstanceId(instanceId);
      if (docNo) {
        const rec = data.find(r => r.documentNumber === docNo);
        if (rec) setSelected(rec);
      }
      setFormMode(m); setFormOpen(true);
    },
    [data],
  );

  const handleConfirmCancel = useCallback(async () => {
    const r = cancelTarget;
    if (!r) return;
    setCancelling(true);
    try {
      await openingStockService.cancel(r.id, r.rowVersion);
      setCancelling(false);
      setCancelTarget(null);
      notificationService.toast.success(`Opening stock ${r.documentNumber} cancelled.`);
      refetch();
    } catch (err: unknown) {
      setCancelling(false);
      setCancelTarget(null);
      notificationService.toast.error(apiMessage(err, `Could not cancel ${r.documentNumber}.`));
      if (apiStatus(err) === 409) refetch();
    }
  }, [cancelTarget, refetch]);

  const columns: SyutGridColumn<OpeningStockDto>[] = useMemo(() => [
    { id: 'documentNumber', header: 'Number', accessorKey: 'documentNumber', size: 150 },
    {
      id: 'documentDate', header: 'Date', accessorKey: 'documentDate', size: 110,
      cell: ({ row }) => fmtDate((row.original as OpeningStockDto).documentDate),
    },
    {
      id: 'businessUnit', header: 'Business unit', accessorKey: 'businessUnitName', size: 180,
      cell: ({ row }) => (row.original as OpeningStockDto).businessUnitName || '—',
    },
    {
      id: 'reference', header: 'Reference', accessorKey: 'reference', size: 160,
      cell: ({ row }) => (row.original as OpeningStockDto).reference || '—',
    },
    {
      id: 'lineCount', header: 'Lines', accessorKey: 'lineCount', size: 80, align: 'right',
      cell: ({ row }) => (row.original as OpeningStockDto).lineCount ?? 0,
    },
    {
      id: 'totalValue', header: 'Value', accessorKey: 'totalValue', size: 140, align: 'right',
      cell: ({ row }) => (
        <span style={{ display: 'block', textAlign: 'right' }}>{money((row.original as OpeningStockDto).totalValue ?? 0)}</span>
      ),
    },
    {
      id: 'valuationTransactionNo', header: 'Valuation voucher', accessorKey: 'valuationTransactionNo', size: 160,
      cell: ({ row }) => (row.original as OpeningStockDto).valuationTransactionNo || '—',
    },
    {
      id: 'docStatus', header: 'Status', accessorKey: 'docStatus', size: 110,
      cell: ({ row }) => statusBadge((row.original as OpeningStockDto).docStatus),
    },
  ], []);

  const rowActions = useCallback((r: OpeningStockDto): ActionMenuItem[] => {
    // Order: View → Edit → Cancel. View is never gated; Edit / Cancel only while posted.
    const items: ActionMenuItem[] = [
      { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(r) },
    ];
    if (r.docStatus === 'POSTED' && can('UPDATE')) {
      items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(r) });
    }
    if (r.docStatus === 'POSTED' && can('DELETE')) {
      items.push({ id: 'cancel', label: 'Cancel', icon: <IconSystem.xCircle size={18} />, onClick: () => setCancelTarget(r), variant: 'danger' });
    }
    return items;
  }, [can, handleView, handleEdit]);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid View' },
  ];

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.add(handleAdd, 'New Opening Stock'), visible: can('CREATE') },
    actionButtonFactory.refresh(refetch),
  ];

  const applyFilters = useCallback(() => {
    setQueryParams(p => ({
      ...p,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      includeCancelled: includeCancelled || undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [fromDate, toDate, includeCancelled]);

  const clearFilters = useCallback(() => {
    setFromDate(''); setToDate(''); setIncludeCancelled(false);
    setQueryParams(p => ({ page: 1, pageSize: p.pageSize, searchTerm: p.searchTerm }));
  }, []);

  const appliedCount = (queryParams.fromDate ? 1 : 0) + (queryParams.toDate ? 1 : 0) + (queryParams.includeCancelled ? 1 : 0);
  const HeaderIcon = moduleIcon ? ICONS[moduleIcon] : undefined;
  const filterLabel: React.CSSProperties = { fontSize: '0.8125rem', marginBottom: '0.25rem' };
  const filterInput: React.CSSProperties = { fontSize: '0.8125rem', padding: '0.375rem 0.625rem' };

  return (
    <EnterpriseScreenShell
      title="Opening Stock"
      description="Quantities and costs of stock held when you start using inventory"
      icon={HeaderIcon ? <HeaderIcon size={18} /> : <IconSystem.upload size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search number / reference / narration...' }}
      viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (m) => setViewMode(m as ViewMode) }}
      filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, appliedCount }}
      customToolbarItems={
        <ColumnSelector columns={COLUMN_DEFS} visibleColumns={visibleColumns}
          onApply={applyColumns} onReset={resetColumns} />
      }
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <label className="form-label" style={filterLabel}>From Date</label>
                <input type="date" className="form-input" value={fromDate} style={filterInput}
                  onChange={e => setFromDate(e.target.value)} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={filterLabel}>To Date</label>
                <input type="date" className="form-input" value={toDate} style={filterInput}
                  onChange={e => setToDate(e.target.value)} />
              </div>
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={includeCancelled}
                    onChange={e => setIncludeCancelled(e.target.checked)} /> Include cancelled
                </label>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <div className="data-grid-container">
        {viewMode === 'list' && (
          <SyutGrid data={data} columns={columns} enableRowSelection
            visibleColumns={[...STRUCTURAL_COLUMN_IDS, ...visibleColumns.filter(id => !STRUCTURAL_COLUMN_IDS.includes(id))]}
            enablePagination manualPagination totalCount={pagination.totalItems}
            currentPage={(queryParams.page ?? 1) - 1} pageSize={queryParams.pageSize ?? 25}
            onPageChange={(i) => setQueryParams(p => ({ ...p, page: i + 1 }))}
            onPageSizeChange={(s) => setQueryParams(p => ({ ...p, pageSize: s, page: 1 }))}
            rowActions={rowActions}
            emptyMessage={loading ? 'Loading...' : 'No opening stock yet — click New Opening Stock.'} />
        )}
        {viewMode === 'grid' && (
          <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{
              padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start',
            }}>
              {data.length === 0 ? (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                  {loading ? 'Loading...' : 'No opening stock yet.'}
                </div>
              ) : data.map(r => (
                <div key={r.id} style={{
                  background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                  borderRadius: '0.75rem', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                        {r.documentNumber} · {fmtDate(r.documentDate)}
                      </div>
                      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0.2rem 0 0' }}>{money(r.totalValue ?? 0)}</h3>
                    </div>
                    <ActionMenu items={rowActions(r)} />
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                    {r.businessUnitName || '—'} · {r.lineCount ?? 0} line{(r.lineCount ?? 0) === 1 ? '' : 's'}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    <span>{r.reference || r.valuationTransactionNo || '—'}</span>
                    {statusBadge(r.docStatus)}
                  </div>
                </div>
              ))}
            </div>
            {data.length > 0 && (
              <div className="pagination-footer">
                <div className="pagination-info">Showing {data.length} of {pagination.totalItems} items</div>
              </div>
            )}
          </div>
        )}
      </div>

      <OpeningStockFormModal isOpen={formOpen} mode={formMode} record={selected}
        onSaved={refetch}
        onClose={() => { setFormOpen(false); setSelected(null); }}
        tabId={tabId} moduleName={moduleName || 'Opening Stock'}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
        onRestoreModal={handleRestoreModal} />

      <OpeningStockViewModal isOpen={viewOpen} record={viewRecord}
        onClose={() => { setViewOpen(false); setViewRecord(null); }}
        onEdit={can('UPDATE') ? handleEdit : undefined}
        tabId={tabId} moduleName={moduleName || 'Opening Stock'}
        onRestoreView={handleRestoreView} />

      <ConfirmDialog
        open={!!cancelTarget}
        title="Cancel Opening Stock"
        message={`Cancel ${cancelTarget?.documentNumber ?? ''}? Its stock and its valuation are removed; later documents are re-costed.`}
        confirmLabel="Cancel Document"
        cancelLabel="Keep"
        variant="danger"
        loading={cancelling}
        loadingMessage="Cancelling..."
        onConfirm={() => { void handleConfirmCancel(); }}
        onCancel={() => { if (!cancelling) setCancelTarget(null); }}
      />
    </EnterpriseScreenShell>
  );
};

export default OpeningStockScreen;
